import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { fetchMoleculeList, fetchMoleculeDetail } from '../services/analyticsService';
import { novaGenerateReport, novaDownloadReport } from '../services/novaService';
import { NovaQuickAnalysisBar } from '../components/shared/NovaQuickAnalysis';
import ReportLanguageToggle, { useReportLanguage } from '../components/shared/ReportLanguageToggle';
import './Suppliers.css';

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const toQuarterLabel = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const q = Math.ceil((dt.getMonth() + 1) / 3);
  return `Q${q} ${dt.getFullYear()}`;
};

const TIMELINE_COLORS = ['#0D5C4F','#0F6B5B','#117A68','#138975','#169882','#18A78F','#1BB69C','#1EC5A9','#25D1B4','#33DBBF'];
const FORM_COLORS = ['#0B3D6B','#0E4A7F','#115893','#1466A7','#1774BB','#1A82CF','#1E8FE0','#239CE8','#2EAAEF','#40B5F3'];

const CHART_CARD = {
  background: 'white',
  borderRadius: 14,
  border: '1px solid #e3e8ef',
  padding: '20px 16px',
  display: 'flex',
  flexDirection: 'column',
};

/* ── Nova Insight Card ── */
function NovaInsight({ text }) {
  if (!text) return null;
  return (
    <div style={{ ...CHART_CARD, padding: '14px 18px', marginBottom: 20, flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
      <span className="ai-badge" style={{ flexShrink: 0, marginTop: 1 }}>✦ Nova</span>
      <div style={{ fontSize: 13, color: 'var(--navy)', lineHeight: 1.55 }}>{text}</div>
    </div>
  );
}

/* ── Supplier Entry Timeline — compact vertical bar chart grouped by quarter ── */
function SupplierTimeline({ suppliers }) {
  const data = useMemo(() => {
    if (!suppliers || suppliers.length === 0) return [];
    // Group suppliers by quarter
    const qMap = new Map();
    for (const s of suppliers) {
      if (!s.firstSeen) continue;
      const q = toQuarterLabel(s.firstSeen);
      if (!qMap.has(q)) qMap.set(q, []);
      qMap.get(q).push(s.supplierName);
    }
    return Array.from(qMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([quarter, names]) => ({ quarter, count: names.length, names: names.join(', ') }));
  }, [suppliers]);

  if (data.length === 0) return null;

  return (
    <div style={{ ...CHART_CARD, marginBottom: 20 }}>
      <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 12 }}>Supplier Entry Timeline</div>
      <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <XAxis dataKey="quarter" tick={{ fontSize: 10, fill: '#6c757d' }} tickLine={false} axisLine={{ stroke: '#e3e8ef' }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6c757d' }} width={30} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: '#fff', border: '1px solid #e3e8ef', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: 260 }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{d.quarter}: {d.count} supplier{d.count !== 1 ? 's' : ''}</div>
                    <div style={{ color: 'var(--t2)', marginTop: 2, wordBreak: 'break-word' }}>{d.names}</div>
                  </div>
                );
              }}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={24}>
              {data.map((_, i) => (
                <Cell key={i} fill={TIMELINE_COLORS[i % TIMELINE_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ── Form Coverage horizontal bars — capped height ── */
function FormCoverage({ forms }) {
  const data = useMemo(() => {
    if (!forms || forms.length === 0) return [];
    return forms
      .filter(f => f.form && f.supplierCount != null)
      .map(f => ({ form: f.form, suppliers: Number(f.supplierCount), firstSeen: toQuarterLabel(f.firstSeen) }));
  }, [forms]);

  if (data.length === 0) return null;

  return (
    <div style={{ ...CHART_CARD, marginBottom: 20 }}>
      <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 12 }}>Form Coverage</div>
      <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <ResponsiveContainer width="100%" height={Math.min(320, Math.max(100, data.length * 36 + 30))}>
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 10 }}>
            <XAxis type="number" tick={{ fontSize: 12, fill: '#6c757d' }} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="form"
              width={130}
              tick={{ fontSize: 12, fill: 'var(--navy)' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              formatter={(val, _name, props) => [`${val} supplier${val !== 1 ? 's' : ''} (since ${props.payload.firstSeen})`, 'Suppliers']}
              contentStyle={{ fontSize: 13, borderRadius: 8 }}
            />
            <Bar dataKey="suppliers" radius={[0, 6, 6, 0]} barSize={20}>
              {data.map((_, i) => (
                <Cell key={i} fill={FORM_COLORS[i % FORM_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function MoleculeDevelopments() {
  const { instance: msalInstance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [atcFilter, setAtcFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedInn, setSelectedInn] = useState(null);
  const [sort, setSort] = useState({ col: 'supplierCount', dir: 'desc' });
  const [exportingPptx, setExportingPptx] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportErr, setExportErr] = useState('');
  const { lang: reportLang, setLang: setReportLang } = useReportLanguage();

  const loadList = useCallback(() => {
    setLoading(true);
    setErr('');
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (atcFilter.trim()) params.atc = atcFilter.trim();
    fetchMoleculeList(msalInstance, params)
      .then(d => setList(d.data || []))
      .catch(() => setErr('Failed to load molecule data'))
      .finally(() => setLoading(false));
  }, [search, atcFilter, msalInstance]);

  useEffect(() => {
    const t = setTimeout(loadList, 300);
    return () => clearTimeout(t);
  }, [loadList]);

  const loadDetail = useCallback((inn) => {
    setSelectedInn(inn);
    setDetailLoading(true);
    fetchMoleculeDetail(msalInstance, inn)
      .then(d => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [msalInstance]);

  // Read ?inn= on mount and load detail if present
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const inn = p.get('inn');
    if (inn) loadDetail(inn);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync URL ↔ selectedInn
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (selectedInn) {
      if (p.get('inn') !== selectedInn) navigate(`?inn=${encodeURIComponent(selectedInn)}`, { replace: true });
    } else if (p.has('inn')) {
      navigate(location.pathname, { replace: true });
    }
  }, [selectedInn, location.search, location.pathname, navigate]);

  const goBack = () => { setSelectedInn(null); setDetail(null); };

  const sorted = useMemo(() => {
    if (!list) return [];
    const arr = [...list];
    arr.sort((a, b) => {
      let av = a[sort.col], bv = b[sort.col];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sort.dir === 'desc' ? bv - av : av - bv;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [list, sort]);

  const toggleSort = (col) => {
    setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' });
  };

  const handleExport = useCallback(async (format) => {
    const setLoading = format === 'pptx' ? setExportingPptx : setExportingXlsx;
    setLoading(true); setExportErr('');
    try {
      const meta = await novaGenerateReport(msalInstance, { reportType: 'molecule', format, filters: { inn: selectedInn }, language: reportLang });
      const blob = await novaDownloadReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = meta.filename || `molecule-report.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setExportErr('Export failed. Please try again.'); }
    finally { setLoading(false); }
  }, [msalInstance, selectedInn, reportLang]);

  const SortTh = ({ col, label }) => {
    const active = sort.col === col;
    return (
      <th className="sup-sort-head" onClick={() => toggleSort(col)}>
        <span style={{ cursor: 'pointer' }}>
          {label}
          {active && <span className="sup-sort-ico">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
        </span>
      </th>
    );
  };

  // ── DETAIL VIEW ──
  if (selectedInn) {
    return (
      <div className="sup-page">
        <div className="mb-3">
          <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>Molecule developments</h2>
          <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
        </div>

        <button
          type="button"
          onClick={goBack}
          style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <i className="bi bi-arrow-left"></i> Back to list
        </button>

        {detailLoading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border spinner-border-sm me-2" role="status" />
            Loading...
          </div>
        )}

        {!detailLoading && detail && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, fontSize: 18, color: 'var(--navy)', margin: 0 }}>{detail.inn}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {exportErr && <span style={{ fontSize: 11, color: 'var(--red)' }}>{exportErr}</span>}
                <ReportLanguageToggle value={reportLang} onChange={setReportLang} compact />
                <button type="button" className="sup-badge teal-soft" disabled={exportingPptx} onClick={() => handleExport('pptx')} style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
                  cursor: exportingPptx ? 'wait' : 'pointer', background: exportingPptx ? '#e8e8e8' : undefined,
                }}>
                  {exportingPptx ? 'Generating...' : 'Download PowerPoint'}
                </button>
                <button type="button" className="sup-badge teal-soft" disabled={exportingXlsx} onClick={() => handleExport('xlsx')} style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
                  cursor: exportingXlsx ? 'wait' : 'pointer', background: exportingXlsx ? '#e8e8e8' : undefined,
                }}>
                  {exportingXlsx ? 'Generating...' : 'Download Excel'}
                </button>
              </div>
            </div>

            {/* Nova Insight */}
            <NovaInsight text={detail.novaSummary} />

            {/* Supplier Entry Timeline — compact column chart */}
            <SupplierTimeline suppliers={detail.suppliers} />

            {/* Form Coverage — capped height */}
            <FormCoverage forms={detail.forms} />

            {/* Supplier timeline table */}
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', marginBottom: 20, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                Supplier Entry Timeline
              </div>
              <div className="sup-table-scroll" style={{ maxHeight: '40vh' }}>
                <table className="sup-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Supplier</th>
                      <th>Form</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.timeline || []).length === 0 ? (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--t3)' }}>No timeline data</td></tr>
                    ) : (
                      detail.timeline.map((t, i) => (
                        <tr key={i}>
                          <td>{fmtDate(t.date)}</td>
                          <td>{t.supplierName || '—'}</td>
                          <td>{t.form || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Suppliers */}
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', marginBottom: 20, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                Suppliers ({(detail.suppliers || []).length})
              </div>
              <div className="sup-table-scroll" style={{ maxHeight: '40vh' }}>
                <table className="sup-table">
                  <thead>
                    <tr>
                      <th>Supplier</th>
                      <th>First Seen</th>
                      <th>Forms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.suppliers || []).map((s, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{s.supplierName}</td>
                        <td>{fmtDate(s.firstSeen)}</td>
                        <td>{(s.forms || []).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Forms */}
            <style>{`
              @keyframes rareFormPulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.4; }
              }
              .rare-form-marker {
                animation: rareFormPulse 1.5s ease-in-out infinite;
                cursor: help;
                margin-left: 6px;
                font-size: 14px;
              }
            `}</style>
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                Form Coverage ({(detail.forms || []).length})
              </div>
              <div className="sup-table-scroll" style={{ maxHeight: '40vh' }}>
                <table className="sup-table">
                  <thead>
                    <tr>
                      <th>Form</th>
                      <th>First Seen</th>
                      <th>Suppliers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.forms || []).map((f, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{f.form}</td>
                        <td>{fmtDate(f.firstSeen)}</td>
                        <td>
                          {f.supplierCount}
                          {f.isRare && (
                            <span
                              className="rare-form-marker"
                              title={`Rare form — only ${f.rareNote || f.supplierCount + ' suppliers'} offer ${f.form}.`}
                            >
                              &#x26A0;&#xFE0F;
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!detailLoading && !detail && (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--t3)' }}>Failed to load detail for {selectedInn}.</p>
          </div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="sup-page">
      <div className="mb-3">
        <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>Molecule developments</h2>
        <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
      </div>

      <NovaQuickAnalysisBar
        placeholder="Quick analysis — type a molecule (INN)..."
        knownEntities={(list || []).map(r => r.inn)}
        entityLabel="molecule"
        reportType="molecule"
        filterBuilder={(entity) => ({ inn: entity })}
        onSubmit={(entity) => { setSearch(entity); loadDetail(entity); }}
      />

      {/* Filters */}
      <div className="sup-filters-row">
        <div>
          <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, color: 'var(--t2)' }}>Search INN</div>
          <input
            className="sup-input"
            style={{ width: 220 }}
            placeholder="Search molecule..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, color: 'var(--t2)' }}>ATC Code</div>
          <input
            className="sup-input"
            style={{ width: 160 }}
            placeholder="Filter ATC..."
            value={atcFilter}
            onChange={(e) => setAtcFilter(e.target.value)}
          />
        </div>
        {(search || atcFilter) && (
          <button type="button" className="sup-clear-link" onClick={() => { setSearch(''); setAtcFilter(''); }}>
            Clear filters
          </button>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Loading molecules...
        </div>
      )}

      {err && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--red)' }}>{err}</p>
        </div>
      )}

      {!loading && !err && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll">
            <table className="sup-table">
              <thead>
                <tr>
                  <SortTh col="inn" label="Molecule (INN)" />
                  <SortTh col="atcCode" label="ATC" />
                  <SortTh col="supplierCount" label="Suppliers" />
                  <SortTh col="formCount" label="Forms" />
                  <SortTh col="firstSeen" label="First Seen" />
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: 'var(--t3)' }}>No molecules found.</td></tr>
                ) : (
                  sorted.map((r) => (
                    <tr key={r.inn} style={{ cursor: 'pointer' }} onClick={() => loadDetail(r.inn)}>
                      <td className="sup-inn">{r.inn}</td>
                      <td>{r.atcCode || '—'}</td>
                      <td><strong>{r.supplierCount}</strong></td>
                      <td>{r.formCount}</td>
                      <td>{fmtDate(r.firstSeen)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {sorted.length > 0 && (
            <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--t3)', borderTop: '1px solid #eef3f7' }}>
              {sorted.length} molecule{sorted.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
